import { MAX_UPLOAD_BYTES } from "@protocol";

/**
 * Checks and metadata for an audio file dropped into the app instead of a live
 * capture. Nothing here decodes the file - ffmpeg on the server does that - so
 * it stays instant however long the recording is.
 */

/** What the file picker offers. Drag and drop is checked by `checkAudioFile`. */
export const AUDIO_ACCEPT =
  "audio/*,video/mp4,video/webm,video/quicktime,video/x-matroska,.m4v,.mp3,.m4a,.aac,.wav,.flac,.ogg,.oga,.opus,.webm,.weba,.mp4,.mov,.mkv,.mka,.wma,.aif,.aiff,.caf,.amr,.3gp";

const EXTENSION_TYPES: Record<string, string> = {
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  aac: "audio/aac",
  wav: "audio/wav",
  flac: "audio/flac",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  opus: "audio/ogg",
  webm: "audio/webm",
  weba: "audio/webm",
  mp4: "video/mp4",
  mov: "video/quicktime",
  mkv: "video/x-matroska",
  mka: "audio/x-matroska",
  wma: "audio/x-ms-wma",
  aif: "audio/aiff",
  aiff: "audio/aiff",
  caf: "audio/x-caf",
  amr: "audio/amr",
  "3gp": "audio/3gpp",
};

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

/**
 * The browser's type when it has one, else a guess from the extension - Windows
 * reports `.m4a` and `.opus` with an empty type more often than not.
 */
export function uploadMimeType(file: File): string {
  if (file.type !== "") return file.type;
  return EXTENSION_TYPES[extensionOf(file.name)] ?? "application/octet-stream";
}

function looksLikeAudio(file: File): boolean {
  if (file.type.startsWith("audio/")) return true;
  if (file.type.startsWith("video/")) return true;
  return extensionOf(file.name) in EXTENSION_TYPES;
}

/** A reason to refuse the file, or null when it can go. */
export function checkAudioFile(file: File, limit: number = MAX_UPLOAD_BYTES): string | null {
  if (!looksLikeAudio(file)) {
    return `"${file.name}" is not an audio file.`;
  }
  if (file.size === 0) return `"${file.name}" is empty.`;
  if (file.size > limit) {
    return `"${file.name}" is ${formatBytes(file.size)} - the limit is ${formatBytes(limit)}.`;
  }
  return null;
}

/** Every file in a drop; empty when the drag carries none. */
export function droppedFiles(transfer: DataTransfer | null): File[] {
  return transfer === null ? [] : Array.from(transfer.files);
}

/** True while dragging something that will arrive as files, not text or links. */
export function dragHasFiles(transfer: DataTransfer | null): boolean {
  return transfer !== null && Array.from(transfer.types).includes("Files");
}

/**
 * Length from the container header, via a detached `<audio>` element. Only the
 * metadata is read, so a two-hour file answers as fast as a short one. Null
 * when the browser cannot play the format - the server measures it anyway.
 */
export function probeDurationMs(file: File, timeoutMs = 4_000): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = document.createElement("audio");
    let settled = false;
    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      audio.removeAttribute("src");
      audio.load();
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const seconds = audio.duration;
      finish(Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds * 1_000) : null);
    };
    audio.onerror = () => finish(null);
    audio.src = url;
  });
}

/**
 * Slice size for the upload socket. Small files get small slices so the
 * progress bar moves smoothly; big ones get 1 MiB so the per-ack round trip is
 * noise next to the transfer itself.
 */
export function chunkBytesFor(fileBytes: number): number {
  const target = Math.ceil(fileBytes / 50);
  return Math.min(1024 * 1024, Math.max(128 * 1024, target));
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    const mb = bytes / (1024 * 1024);
    return `${mb >= 100 ? Math.round(mb) : mb.toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
